import { Injectable } from "@angular/core";
import { PaymentMethod } from "../enums/payment-method.enum";
import { TransactionType } from "../enums/transaction-type.enum";
import { RegistrationDetailTransaction } from "../interfaces/registration-detail-transaction.interface";
import { RegistrationDetail } from "../interfaces/registration-detail.interface";
import { RegistrationPaymentMapping } from "../interfaces/registration-payment-mapping.interface";
import { RegistrationProfileService } from "./registration-profile.service";
import { RegistrationDropMapping } from "../interfaces/registration-drop-mapping.interface";
import { falsey } from "@edtell-common";

// Used by the edtell-header module, provided globally
@Injectable({
  providedIn: "root",
})
export class RegistrationHeaderService {
  constructor(private registrationProfileService: RegistrationProfileService) {}

  getRevenueInHand() {
    return this.registrationProfileService
      .getPayments()
      .filter(
        (payment) =>
          payment.paymentMethod == PaymentMethod.CREDIT_CARD ||
          payment.paymentMethod == PaymentMethod.REFUND ||
          ((payment.paymentMethod == PaymentMethod.CHECK_OR_MONEY_ORDER ||
            payment.paymentMethod == PaymentMethod.PAYMENT_CODE) &&
            payment.paymentReceivedDate),
      )
      .reduce((acc, curr) => acc + curr.saleTotal, 0);
  }

  getRevenueInHandTax() {
    return this.registrationProfileService
      .getPayments()
      .filter(
        (payment) =>
          payment.paymentMethod == PaymentMethod.CREDIT_CARD ||
          payment.paymentMethod == PaymentMethod.REFUND ||
          ((payment.paymentMethod == PaymentMethod.CHECK_OR_MONEY_ORDER ||
            payment.paymentMethod == PaymentMethod.PAYMENT_CODE) &&
            payment.paymentReceivedDate),
      )
      .reduce((acc, curr) => acc + curr.saleTax, 0);
  }

  getParentPaymentPending() {
    return this.registrationProfileService
      .getPayments()
      .filter(
        (payment) =>
          payment.paymentMethod == PaymentMethod.CHECK_OR_MONEY_ORDER &&
          !payment.paymentReceivedDate,
      )
      .reduce((acc, curr) => acc + curr.saleTotal, 0);
  }

  getSchoolPaymentPending(paymentCodeInfo) {
    return this.registrationProfileService
      .getPayments()
      .filter(
        (payment) =>
          payment.paymentMethod == PaymentMethod.PAYMENT_CODE &&
          !payment.paymentReceivedDate &&
          (!paymentCodeInfo.hasOwnProperty(payment.paymentCode.toLowerCase()) ||
            !paymentCodeInfo[payment.paymentCode.toLowerCase()].hasLicense),
      )
      .reduce((acc, curr) => acc + curr.saleTotal, 0);
  }

  getPaymentOwed() {
    return this.registrationProfileService
      .getDetails()
      .reduce((acc, detail: RegistrationDetail) => {
        acc =
          acc +
          this.registrationProfileService.getAdminDrop(detail) +
          this.registrationProfileService.getAdminWithNegativeAdjustments(
            detail,
          );
        const dropAmount: number =
          this.registrationProfileService.getDropped(detail);
        const courseCost: number =
          this.registrationProfileService.getTotalInitialCost(detail) +
          this.registrationProfileService.getAdminWithoutDrop(detail) +
          this.registrationProfileService.getAdminWithNegativeAdjustments(
            detail,
          );

        const dropTransaction: RegistrationDetailTransaction =
          detail.transactions.find(
            (transaction) =>
              transaction.transType == TransactionType.DROP &&
              transaction.saleAmount - transaction.adminAmount > 0,
          );

        const paidAmount: number = this.registrationProfileService
          .getPayments()
          .reduce((paymentAcc, currentPayment) => {
            return (
              paymentAcc +
              currentPayment.mappings
                .filter(
                  (mapping: RegistrationPaymentMapping) =>
                    mapping.regDetailId == detail.id,
                )
                .reduce((mappingAcc, currentMapping) => {
                  // Removes admin payment owed from acc
                  const paysForDropAdminFee: boolean =
                    dropTransaction &&
                    currentMapping.regDetailTransId == dropTransaction.id;
                  if (paysForDropAdminFee && (+currentMapping.adminAmount) > 0) {
                    acc = +currentMapping.adminAmount - acc
                  } else {
                    mappingAcc += +currentMapping.adminAmount;
                  }
                  return mappingAcc + +currentMapping.saleAmount;
                }, 0)
            );
          }, 0);
        return (
          acc + Math.abs(Math.min(0, dropAmount - (courseCost - paidAmount)))
        );
      }, 0);
  }

  getUnallocated() {
    const paymentMappings: RegistrationPaymentMapping[] = this.registrationProfileService
      .getPayments().reduce((acc, curr) => [ ...acc, ...curr.mappings ], [])
    const dropMappings: RegistrationDropMapping[] = this.registrationProfileService
      .getDropMappings();
    const details: RegistrationDetail[] = this.registrationProfileService.getDetails();
    return paymentMappings.reduce((acc, curr) => {
      const detail: RegistrationDetail = details.find(d => d.id == curr.regDetailId);
      const adjustmentAmount: number = detail.transactions
        .filter(t => t.transType == TransactionType.ADJUST)
        .reduce((acc, curr) => acc + curr.saleAmount, 0)
      let dropAmount: number = dropMappings.find(dm => 
          dm.droppedRegDetailId == detail.id
          && falsey(dm.droppedTransactionId)
          && !dm.isAdmin
        )?.moneyAvailableFromDrop ?? 0;
      let itemCost: number = detail.licenseValue > 0 ? 0 : detail.saleAmount + adjustmentAmount;
      if(!falsey(curr.regDetailTransId)) {
        const transaction: RegistrationDetailTransaction = detail.transactions.find(t => t.id == curr.regDetailTransId);
        itemCost = transaction.saleAmount;
        dropAmount = dropMappings.find(dm => 
            dm.droppedRegDetailId == detail.id
            && dm.droppedTransactionId == transaction.id
            && !dm.isAdmin
          )?.moneyAvailableFromDrop ?? 0
        if(+curr.adminAmount > 0) {
          itemCost = transaction.adminAmount;
          dropAmount = dropMappings.find(dm => 
              dm.droppedRegDetailId == detail.id
              && dm.droppedTransactionId == transaction.id
              && dm.isAdmin
            )?.moneyAvailableFromDrop ?? 0
        }
      }

      const paidAmount: number = +curr.adminAmount != 0 ? +curr.adminAmount : +curr.saleAmount
      return acc + paidAmount - (itemCost - dropAmount);
    }, 0) 
  }
}
